import dynamic from 'next/dynamic'
import useTranslation from 'next-translate/useTranslation'
import { twMerge } from 'tailwind-merge'

import { Button } from '@unreserved-frontend-v2/ui/button/button'
import { ConditionalContainer } from '@unreserved-frontend-v2/ui/conditional-container/conditional-container'
import { DetailsDescription } from '@unreserved-frontend-v2/ui/details-description/details-description'
import { DetailsTable } from '@unreserved-frontend-v2/ui/details-table/details-table'
import { Previewer } from '@unreserved-frontend-v2/ui/previewer/previewer'
import { RestrictableContent } from '@unreserved-frontend-v2/ui/utils/types'
import {
  PropertyListingStatus,
  ResidentialPropertyForSaleListing,
} from '@unreserved-frontend-v2/api/generated/graphql/types'
import { useData } from './utils/use-data'
import { RegisterOrSignInPromptProps } from '../../../../users/components/register-or-sign-in/register-or-sign-in-prompt'
import { Trademark } from '../../../../real-estate/trademark'
import { ListingPreviewerBadges } from '../../listing-previewer/previewer-badges'
import { AskAQuestion } from '../../ask-a-question/ask-a-question'
import { DEFAULT_CONTACT_NUMBER, isForSale } from '../../../utils/routing/constants'
import { formatListingDetailsUrl } from '../../../utils/routing/parse-format-details-slugged-values'
import { ReactNode } from 'react'
import Link from 'next/link'
import { SubNavTabs } from '../use-submenu-info'
import { ReactFunctionalComponent } from '@unreserved-frontend-v2/utils/types'
import { getParsedNearbyListings } from '@unreserved-frontend-v2/modules/listings/utils/get-parsed-nearby-listings'
import { ListingCardsGroupVariant } from '../../listing-cards-group/listing-cards-group'
import { SimpleMapProps } from '@unreserved-frontend-v2/geolocation/components/map/simple-map'
import { getSimpleAddress } from '@unreserved-frontend-v2/geolocation/utils/utils'

const RegisterOrSignInPrompt: React.ComponentType<RegisterOrSignInPromptProps> = dynamic(
  () => import('../../../../users/components/register-or-sign-in/register-or-sign-in-prompt')
)

const SimpleMap: React.ComponentType<SimpleMapProps> = dynamic(
  () => import('@unreserved-frontend-v2/geolocation/components/map/simple-map').then((mod) => mod.SimpleMap),
  { ssr: false }
)

const DescriptionSummary = dynamic(() =>
  import('@unreserved-frontend-v2/ui/description-summary/description-summary').then((mod) => mod.DescriptionSummary)
) as ReactFunctionalComponent

const VirtualTourBox = dynamic(() => import('../../virtual-tour-box/virtual-tour-box'))

const Rooms = dynamic(() => import('../../details/rooms'))

const CashbackIncentive = dynamic(() =>
  import('../../cashback-incentive').then((mod) => mod.CashbackIncentive)
) as ReactFunctionalComponent

const BookShowing = dynamic(() =>
  import('../../book-a-showing/book-a-showing').then((mod) => mod.BookShowing)
) as ReactFunctionalComponent

const OpenHouseSchedule = dynamic(() =>
  import('../../details/open-house/open-house-schedule').then((mod) => mod.OpenHouseSchedule)
) as ReactFunctionalComponent

const ListingCardsGroup = dynamic(() => import('../../listing-cards-group/listing-cards-group'))

export interface OverviewDesktopProps extends RestrictableContent {
  listing: ResidentialPropertyForSaleListing
  id: string
  scrolledHeader?: boolean
  rightColumnComponent?: ReactNode
}

export function OverviewDesktop({
  listing,
  scrolledHeader = false,
  isRestricted,
  rightColumnComponent,
}: OverviewDesktopProps) {
  const { ownershipDetailsRows, propertyDetailsRows, mlsDetailsRows } = useData(listing)
  const { t } = useTranslation()

  /**
   * For Sale listings will show a cashback incentive, which we don't want to sticky, we only want to sticky the map and the right column contents below
   * Sold listings don't have a cashback incentive, so we need to adjust the positioning accordingly. And since a line fades in in the header, we need to offset the top value for the scrolled header
   */
  const rightColumnClasses = isForSale(listing.listingStatus)
    ? 'sticky -top-[258px] mb-10'
    : `transition-position sticky duration-150 mb-10 ${scrolledHeader ? 'top-[158px]' : 'top-[144px]'}`

  const hasSummary = !!listing.descriptionSummary
  const { forSaleListings, soldListings } = getParsedNearbyListings(listing.nearbyListings || [])

  const previewerContents = (
    <>
      <Previewer
        images={listing ? listing.media?.map((m) => m.originUrl) : []}
        isRestricted={isRestricted}
        restrictedPrompt={<RegisterOrSignInPrompt isSold={listing.listingStatus === PropertyListingStatus.Sold} />}
        altPrefix={getSimpleAddress(listing?.property.address)}
      />
      <div className="pointer-events-none absolute bottom-0 mb-4 flex w-full">
        {!isRestricted ? (
          <div className="ml-4 mr-6 flex-grow">
            <ListingPreviewerBadges listing={listing} />
          </div>
        ) : null}
        {listing.media && listing.media.length < 3 ? null : <div className="w-1/3"></div>}
      </div>
    </>
  )

  return (
    <>
      <div className="grid grid-cols-11 gap-x-6" data-nosnippet={isRestricted}>
        <div className="col-span-8">
          {!isRestricted ? (
            <Link
              data-testid="photo-preview"
              href={`${formatListingDetailsUrl(listing)}/${SubNavTabs.MEDIA}`}
              className={twMerge('relative', !isRestricted ? 'cursor-pointer' : '')}
            >
              {previewerContents}
            </Link>
          ) : (
            <div className="relative">{previewerContents}</div>
          )}
          <ConditionalContainer isContainerized={hasSummary} type="div" containerClassName="grid grid-cols-12">
            <>
              {hasSummary && !isRestricted ? (
                <DescriptionSummary
                  columns={1}
                  descriptionSummary={listing?.descriptionSummary as string[]}
                  isRestricted={undefined}
                  className="border-shades-200 col-span-4 mt-8 border-r border-dashed py-0 pr-6 pl-0 [&>summary]:text-lg"
                />
              ) : null}
              <DetailsDescription
                description={listing.description as string}
                className="mt-8 leading-[26px]"
                containerClassName={hasSummary ? 'pl-8 col-span-8' : ''}
                isRestricted={isRestricted}
                isOpen={hasSummary}
              />
            </>
          </ConditionalContainer>
          {listing.propertyDetails?.virtualTourUrl ? (
            <VirtualTourBox className="mt-8" url={listing.propertyDetails?.virtualTourUrl} />
          ) : null}
          <div className="my-8">
            <h2 className="mt-8 mb-5 text-lg font-bold leading-6">{t('listings:details-pages.property-details')}</h2>
            <DetailsTable rows={propertyDetailsRows} showMoreInitially={true} isRestricted={isRestricted} />
          </div>

          {listing.rooms?.length ? (
            <div className="mb-8">
              <Rooms
                listingId={listing.id}
                rooms={listing.rooms}
                isEdittable={false}
                isRestricted={isRestricted}
                is3DAllowed={true}
              />
            </div>
          ) : null}

          {ownershipDetailsRows.length ? (
            <div className="mt-8 mb-10">
              <h2 className="mt-8 mb-5 text-lg font-bold leading-6">{t('listings:details-pages.ownership-details')}</h2>
              <DetailsTable rows={ownershipDetailsRows} showMoreInitially={true} isRestricted={isRestricted} />
            </div>
          ) : null}

          <div className="mt-8">
            <h2 className="mt-8 mb-5 text-lg font-bold leading-6">{t('listings:details-pages.accreditation')}</h2>
            <DetailsTable rows={mlsDetailsRows} showMoreInitially={true} isRestricted={isRestricted} />
          </div>
        </div>

        <div className="relative col-span-3">
          {rightColumnComponent ? (
            rightColumnComponent
          ) : (
            <div className={twMerge(rightColumnClasses, !listing.buyerCashback ? 'top-[158px] mb-0' : '')}>
              {listing.buyerCashback ? <CashbackIncentive listing={listing} className="mb-8" /> : null}
              <div className="border-shades-200 mb-8 h-[328px] overflow-hidden rounded-md border">
                <SimpleMap
                  mapId={`simple-listing-${listing.id}`}
                  center={{ lng: listing.property.address.longitude, lat: listing.property.address.latitude }}
                  markers={[
                    { coordinate: { lng: listing.property.address.longitude, lat: listing.property.address.latitude } },
                  ]}
                />
              </div>

              <Button href={`tel:${DEFAULT_CONTACT_NUMBER}`} variant="ghost" className="mb-2.5">
                {t('listings:call-us', { number: DEFAULT_CONTACT_NUMBER })}
              </Button>
              <AskAQuestion className="mb-2.5" listing={listing} variant="button-secondary" />
              {isForSale(listing.listingStatus) ? <BookShowing className="mb-2.5" listing={listing} /> : null}
              {listing.upcomingOpenHouses ? (
                <OpenHouseSchedule openHouses={listing.upcomingOpenHouses} className="mb-2.5" />
              ) : null}
            </div>
          )}
        </div>
      </div>
      <ListingCardsGroup
        variant={ListingCardsGroupVariant.COLUMNS}
        cardClassName={'col-span-3'}
        className={'pt-8'}
        title={t('listings:nearby-listings.nearby-for-sale-title')}
        listings={forSaleListings}
        headerTagType="h2"
        listingCardHeaderTagType="span"
      />

      <ListingCardsGroup
        variant={ListingCardsGroupVariant.COLUMNS}
        cardClassName={'col-span-3'}
        className="pt-5"
        title={t('listings:nearby-listings.nearby-sold-title')}
        listings={soldListings}
        headerTagType="h2"
        listingCardHeaderTagType="span"
      />
      <Trademark source={listing.source} className="mt-4 mb-8" />
    </>
  )
}

export default OverviewDesktop
